import { useEffect } from "react";
import { atom, useRecoilState } from "recoil";
import { filter } from "rxjs/operators";

import { tracks$ } from "./TracksStream";
import { options$ } from "./OptionsStream";
import { commands$ } from "./CommandsStream";
import { EDITOR, NO_LOOP, LOOP_PLAYLIST, LOOP_TRACK } from "./Config";
import { JUMP, PLAY, PAUSE, STOP, NEXT, PREVIOUS } from "./CommandsStream";

import { BuffersLoader } from "./BuffersLoader";

const playerCommands$ = commands$.stream.pipe(
  filter(({ action }) =>
    [JUMP, PLAY, PAUSE, STOP, NEXT, PREVIOUS].includes(action)
  )
);

export const playingTrack = atom({
  key: "playingTrack",
  default: { url: null, position: 0 },
});

export const sequencerReady = atom({
  key: "sequencerReady",
  default: false,
});

export const playbackState = atom({
  key: "playbackState",
  default: "idle",
});

export function useSequencerReady() {
  const [isReady, setReady] = useRecoilState(sequencerReady);

  useEffect(() => {
    const done = () => {
      setReady(true);
    };

    document.addEventListener("sequencer:ready", done);

    return () => {
      document.removeEventListener("sequencer:ready", done);
    };
  }, [setReady]);

  return isReady;
}

export function usePlaybackState() {
  const [state, setState] = useRecoilState(playbackState);

  useEffect(() => {
    const stopped = () => setState("idle");
    const paused = () => setState("paused");
    const playing = () => setState("playing");
    const restart = () => setState("playing");

    document.addEventListener("sequencer:paused", paused);
    document.addEventListener("sequencer:start", playing);
    document.addEventListener("sequencer:ended", stopped);
    document.addEventListener("sequencer:stopped", stopped);
    document.addEventListener("sequencer:continue", restart);

    return () => {
      document.removeEventListener("sequencer:paused", paused);
      document.removeEventListener("sequencer:start", playing);
      document.removeEventListener("sequencer:ended", stopped);
      document.removeEventListener("sequencer:stopped", stopped);
      document.removeEventListener("sequencer:continue", restart);
    };
  }, [setState]);

  return state;
}

const dummyAudio = {
  stop: () => { },
  skip: (_at) => { },
  on: () => { },
  unAll: () => { },
  loadDecodedBuffer: () => { },
  play: () => { },
  pause: () => { },
  setVolume: () => { },
};

let singleton = null;

class Sequencer {
  static create(playlist, audiCache) {
    singleton = new Sequencer(playlist, audiCache);
  }

  static listUpdated(list) {
    if (singleton === null) return;

    singleton.playlist = list;
  }

  static select(track) {
    if (singleton === null) return;

    singleton.select(track);
  }

  static changeVolume(track) {
    if (singleton === null) return;

    singleton.changeVolume(track);
  }

  static setAudioInstance(instance) {
    if (singleton === null) return;

    singleton.audioInstance = instance;
  }

  static dispose() {
    if (singleton === null) return;

    const sequencer = singleton;

    singleton = null;

    sequencer.dispose();
  }

  constructor(playlist, audiCache) {
    this.playbackState = "idle";

    this.playlist = playlist;

    this.loopMode = NO_LOOP;

    this.current = null;
    this.cachedBuffer = {};

    this.loadAudioData(audiCache);

    this.audio = dummyAudio;

    this.subscriptionC = playerCommands$.subscribe(({ action, at }) => {
      switch (action) {
        case PLAY:
          this.play();
          break;
        case PAUSE:
          this.pause();
          break;
        case NEXT:
          this.next();
          break;
        case PREVIOUS:
          this.previous();
          break;
        case STOP:
          this.stop();
          this.triggerPlaying(null);
          break;
        case JUMP:
          this.jump(at);
          break;
        default:
      }
    });

    this.subscriptionOpt = options$.stream.subscribe(({ loopMode }) => {
      if (loopMode) this.loopMode = loopMode;
    });
  }

  dispose() {
    this.subscriptionC.unsubscribe();
    this.subscriptionOpt.unsubscribe();
  }

  set playlist(list) {
    this.list = list;
  }

  set audioInstance(instance) {
    this.audio = instance;
  }

  isPlaying = () => this.playbackState === "playing";
  isPaused = () => this.playbackState === "paused";

  jump = (at) => {
    const current = this.audio.getCurrentTime();

    this.audio.skip(at - current);
  };

  play = () => {
    if (this.isPlaying()) return;

    if (this.isPaused()) {
      this.audio.play();

      this.triggerContinue();

      return;
    }

    this.triggerStart();

    this.current = this._findNext(this.current);

    this._playCurrent();
  };

  pause = () => {
    if (this.isPaused()) return;

    if (!this.isPlaying()) return;

    this.playbackState = "paused";

    this.triggerPaused();

    this.audio.pause();
  };

  stop = () => {
    if (!this.audio) return;

    this.audio.stop();

    this.playbackState = "idle";

    this.triggerStopped();

    this.current = null;
  };

  next = () => {
    this.finder = this._findNext;

    this.current = this._findNext(this.current);

    this._playCurrent();
  };

  previous = () => {
    this.finder = this._findPrevious;

    this.current = this._findPrevious(this.current);

    this._playCurrent();
  };

  select = (track) => {
    if (track.url === this.current) return;

    this.audio.stop();

    this.finder = () => track.url;

    this.current = track.url;

    this._playCurrent();
  };

  loadAudioData = (audiCache) => {
    this.buffers = null;
    this.context = new AudioContext();

    const doneLoading = (buffers) => {
      this.buffers = buffers;

      this.triggerReady();
    };

    const files = this.list.map((track) => track.url);

    new BuffersLoader(this.context, files, {
      onLoaded: this.triggerLoaded,
      onError: this.triggerError,
      onReady: doneLoading,
    }).loadMetadata(audiCache);
  };

  triggerLoaded = (url, duration) => {
    var event = new CustomEvent("sequencer:loaded", {
      detail: { url, duration },
    });

    document.dispatchEvent(event);
  };

  triggerError = (url) => {
    var event = new CustomEvent("sequencer:load-error", {
      detail: { url },
    });

    document.dispatchEvent(event);
  };

  triggerReady = (url) => {
    var event = new CustomEvent("sequencer:ready");

    document.dispatchEvent(event);
  };

  triggerPlaying = (track) => {
    if (track && this.playbackState === "idle") {
      this.triggerStart();
    }

    var event = new CustomEvent("sequencer:playing", {
      detail: { url: track ? track.url : null },
    });

    document.dispatchEvent(event);
  };

  triggerStart = () => {
    this.playbackState = "playing";

    var event = new CustomEvent("sequencer:start");

    document.dispatchEvent(event);
  };

  triggerPaused = () => {
    var event = new CustomEvent("sequencer:paused");

    document.dispatchEvent(event);
  };

  triggerContinue = () => {
    this.playbackState = "playing";

    var event = new CustomEvent("sequencer:continue");

    document.dispatchEvent(event);
  };

  triggerStopped = () => {
    var event = new CustomEvent("sequencer:stopped");

    document.dispatchEvent(event);
  };

  triggerEnded = () => {
    var event = new CustomEvent("sequencer:ended");

    document.dispatchEvent(event);
  };

  triggerPosition = (url, title, position) => {
    const duration = this.audio.getDuration();

    var event = new CustomEvent("sequencer:position", {
      detail: { url, title, duration, position },
    });

    document.dispatchEvent(event);
  };

  changeVolume = (track) => {
    const fraction = track.volume / 100;

    // Let's use an x*x curve (x-squared) since simple linear (x) does not
    // sound as good.
    const gain = fraction * fraction;

    this.audio.setVolume(gain);
  };

  _findNext = (current) => {
    const from =
      current === null ? 0 : this.list.findIndex((e) => e.url === current) + 1;

    const i = this.list.slice(from).findIndex((e) => e.enabled && !e.error);

    return i < 0 ? null : this.list[from + i].url;
  };

  _findPrevious = (current) => {
    const reversed = [...this.list].reverse();

    const from =
      current === null ? 0 : reversed.findIndex((e) => e.url === current) + 1;

    const i = reversed
      .slice(from)
      .findIndex((e) => e.infoLoaded && e.enabled && !e.error);

    return i < 0 ? null : reversed[from + i].url;
  };

  _playCurrent = () => {
    if (this.current === null) {
      this._cancelCurrent();

      return;
    }

    const track = this.list.find((e) => e.url === this.current);

    this._loadAndPlay(track);
  };

  _loadAndPlay = (track) => {
    if (this.cachedBuffer[track.url]) {
      this._playBuffer(track, this.cachedBuffer[track.url]);
      this._preloadNext();

      return;
    }

    tracks$.select(track);

    new BuffersLoader(this.context, [track.url], {
      onLoaded: (_url, _duration, buffer) => {
        this._playBuffer(track, buffer);
        this._preloadNext();
      },
      onError: () => this._cancelCurrent(),
      onReady: () => { },
    }).load();
  };

  _cancelCurrent = () => {
    this.audio.stop();

    this.playbackState = "idle";

    this.triggerPlaying(null);
    this.triggerEnded();
  };

  _playTrack = (track) => {
    this._playBuffer(track, this.buffers[track.url]);
  };

  _playBuffer = (track, buffer) => {
    this.audio.unAll();

    this.audio.setVolume(track.volume / 100);
    this.audio.loadDecodedBuffer(buffer);

    this.audio.on("audioprocess", (pos) => {
      this.triggerPosition(track.url, track.title, pos);
    });

    this.audio.on("seek", (progress) => {
      const pos = this.audio.getDuration() * progress;

      this.triggerPosition(track.url, track.title, pos);
    });

    this.audio.on("finish", () => {
      if (this.current === null) return;

      if (this.loopMode === EDITOR) {
        this.pause();
        return;
      }

      if (this.loopMode === LOOP_TRACK) {
        this.cachedBuffer[track.url] = buffer;

        this._playCurrent();

        return;
      }

      this.current = this.finder(this.current);

      if (this.current === null && this.loopMode === LOOP_PLAYLIST) {
        this.current = this.finder(null);
      }

      this._playCurrent();

      this.finder = this._findNext;
    });

    this.triggerPlaying(track);

    this.finder = this._findNext;

    this.audio.play();
  };

  _preloadNext = () => {
    this.cachedBuffer = {};

    const nextURL = this._findNext(this.current);

    if (!nextURL) return;

    new BuffersLoader(this.context, [nextURL], {
      onLoaded: (url, _duration, buffer) => {
        this.cachedBuffer[url] = buffer;
      },
      onError: () => { },
      onReady: () => { },
    }).load();
  };
}

export { Sequencer };
